<template>
    <article class="question-display">
        <div class="sr-only">
            <p>
							{{ $t('MAIN_CONTENT.testTaker.questionNumber', { number: activeQuestion.SEQUENCE } ) }}
						</p>
        </div>

        <div class="pull-right">
            <span class="pill pill-dark">
							{{ $t('MAIN_CONTENT.MultipleChoiceQuestion.pointsPossible', { points: activeQuestion.TOTAL_POINTS } ) }}
						</span>
        </div>

        <div class="question-type-instructions">
            <strong>{{ activeQuestion.QUESTION_NAME }}</strong>
            <span class="hint" v-if="multipleAnswers">
							{{ $t('MAIN_CONTENT.MultipleChoiceQuestion.selectAllMessage') }}
						</span>
            <span class="hint" v-else>
							{{ $t('MAIN_CONTENT.MultipleChoiceQuestion.selectCorrectMessage') }}
						</span>
            <span class="hint" v-if="activeQuestion.ALLOW_NEGATIVE_TOTAL_POINTS_FLG">
							{{ $t('MAIN_CONTENT.MultipleChoiceQuestion.incorrectMessage') }}
						</span>
        </div>

        <h3
            class="question-text"
            v-html="activeQuestion.QUESTION_TEXT" />

        <div class="answers">
            <!-- note the double event handlers - one for click and one for keyboard -->
            <div
                v-for="( answer, index ) in activeQuestionAnswers" 
                :key="index" 
                :class="getAnswerClass( answer )"
                tabindex="0"
                @click="answerTheQuestion( answer )"
                @keyup.enter="answerTheQuestion( answer )">
                <div :class="[ 'answer-number',{ selected:isSelected( answer ) } ]">
                    <i
                        v-if="answeredByStudent( answer )"
                        class="fa fas fa-arrow-circle-right" 
                        :aria-label="'Selected Answer ' + getAnswerChoice( index )" aria-live="polite" />
                    {{ getAnswerChoice( index ) }}
                    <div
                        v-if="isGraded() && answeredByStudent( answer )"
                         class="badge badge-dark answer-points float-right"
                        aria-label="Points Earned">
											{{ $t('MAIN_CONTENT.testTaker.points', { points: getPointsEarned(answer) } ) }}
                    </div>
                </div>
                <div
                    class="answer-text"
                    v-html="answer.ANSWER_TEXT" />
                <div class="grade-result">
                    <i :class="getFeedbackIcon( answer )" />
                </div>
            </div>
        </div>

        <question-summary :answerData="answerData" />

        <test-buttons v-if="testMode !== 'grading'"
            @answer-save="submitAnswer" />
    </article>
</template>
<script>
import { mapState,mapActions,mapMutations } from "vuex";
import testButtons from "./testButtons.vue";
import questionSummary from "./questionSummary.vue";

export default{
    components : { testButtons, questionSummary },
    props      : {
        testPreview: {
            type     : Object,
            required : true
        },
        activeQuestion: {
            type     : Object,
            required : true
        },
        studentAnswer: {
            type    : Array,
            default : function(){
                return [];
            }
        },
        studentAnswers:{
            type    : Object,
            default : function(){
                return {};
            }
        },
        testMode: {
            type    : String,
            default : "student"
        }
    },
    data (){
        return{
            // local object to manage selected answer(s)
            answerData : {
                questionId : "",
                answer     : []
            }
        };
    },
    computed: {
        allowChanges (){
            return this.testPreview.TEST.ALLOW_ANSWER_CHANGE;
        },
        activeQuestionAnswers (){
            return this.testPreview.ANSWERS.filter( ans =>{
                return ans.TEST_QUESTION_RID === this.activeQuestion.TEST_QUESTION_RID; 
            } );
        },
        multipleAnswers (){
            return this.activeQuestion.MULTIPLE_ANSWER_FLG;
        },
        pointsEarned (){
            let total = 0;
            this.studentAnswer.forEach( ans =>{
                total += ans.POINTS_EARNED;
            } );
            return total;
        }
    },
    watch: {
        activeQuestion: {
            immediate: true,
            handler: function(newValue, oldValue) {
                this.$set(this.answerData, "questionId" , this.activeQuestion.TEST_QUESTION_RID);
                this.$set(this.answerData, "testId", this.activeQuestion.TEST_RID);
                this.$set(this.answerData, "answer", JSON.parse( JSON.stringify( this.studentAnswer ) ));
                this.$nextTick(()=>{
                    try{
                        document.getElementById( "testZone" ).focus();
                    }catch(e){}
                })
                
            },
        },
    },
    methods: {
        ...mapMutations( [ "updateState","setUnsaved" ] ),
        ...mapActions( [ "update","scoreAnswer","postAnswer" ] ),
        isSelected ( answer ){
            return this.studentAnswer.map( ans =>{
                return ans && !!ans.ANSWER_RID ? ans.ANSWER_RID.toString(): ''; 
            } ).indexOf( answer.ANSWER_RID.toString() ) > -1;
        },
        getPointsEarned(answer){
            let points = 0;
            let _ans = this.studentAnswer.filter( ans =>{
                return ans.ANSWER_RID.toString() === answer.ANSWER_RID.toString(); 
            } );
            if(_ans.length === 1){
                points = _ans[0].POINTS_EARNED;
            }
            return points;
        },
        // will return css class for check if correct and x for incorrect for graded answers
        getFeedbackIcon ( answer ){
            if( !this.isGraded() ) return;
            
            return this.getPointsEarned(answer) > 0 || this.isCorrect ( answer ) ? "fa fas fa-check-circle" :"fa fas fa-times-circle";
            
        },
        // returns css class if graded correct
        getAnswerClass ( answer ){
            let cls = "answer";
            if( !this.isGraded() || this.allowChanges ){
                cls += " clickable";
            }
            if( this.isGraded() ){
                cls += this.getPointsEarned(answer) > 0 || this.isCorrect ( answer ) ? " correct" : " incorrect";
            }
            return cls;
        },
        isGraded (){
            return this.testPreview.TEST.TEST_ENDED_FLG || this.studentAnswer && this.studentAnswer.filter( ans =>{
                return ans.QUESTION_GRADED_STATUS; 
            } ).length > 0;
        },
        // compares answer against answer key
        isCorrect ( answer ){
            if( ! this.isGraded() ) return;
            return answer.ANSWER_CORRECT_FLG;
        },
        // did the student answer question
        answeredByStudent ( answer ){
            return this.studentAnswer.map( ans => {
                return ans && !!ans.ANSWER_RID ? ans.ANSWER_RID.toString(): ''; 
            } ).indexOf( answer.ANSWER_RID.toString() ) > -1;
        },
        // returns either letter, number or roman numeral for the answer choice
        getAnswerChoice ( index ){
            return this.numToLetters(index+1);
        },
        numToLetters(v) {
            let result = [];
            let accum = Number(v);
            if (accum < 1) {
                return "";
            }
            while (accum>0) {
                let rem = (accum-1)%26;
                result.splice(0,0,String.fromCharCode(65+rem));
                accum = (accum - rem - 1)/26;
            }
            return result.join("");
        },
        getAnswerChoiceNumber ( answerId ){
            let index = this.activeQuestionAnswers.map( ans => {
                return ans.ANSWER_RID; 
            } ).indexOf( answerId );
            return this.getAnswerChoice( index );
        },
        prepareAnswer ( answer ){
            // prepare payload with activeQuestion id to use as a key
            this.answerData.questionId = this.activeQuestion.TEST_QUESTION_RID;
						this.answerData.testId = this.activeQuestion.TEST_RID;
						this.answerData.questionType = this.activeQuestion.QUESTION_TYPE;
            this.answerData.answer = JSON.parse( JSON.stringify( this.studentAnswer ) );

            // if question has not been answered, add the answer
            if( !this.answerData.answer.length ){
                this.answerData.answer.push( answer );
            }else{
                // if answer is clicked and is already selected, deselect
                let existingIndex = this.answerData.answer.map( ans =>{
                    return ans.ANSWER_RID; 
                } ).indexOf( answer.ANSWER_RID );
                if( existingIndex > -1 ){
                    this.answerData.answer.splice( existingIndex, 1 );
                }else{
                    if( this.activeQuestion.MULTIPLE_ANSWER_FLG ){
                        this.answerData.answer.push( answer );
                    }else{
                        this.answerData.answer = [];
                        this.answerData.answer.push( answer );
                    }
                }
            }
        },
        answerTheQuestion ( answer ){
            // question has been answered, so no changes
						if( this.isGraded() && !this.allowChanges ) return;
						
						// save the answer
						this.prepareAnswer( answer );
						
            // tracking unsaved responses
            this.setUnsaved( { key: this.activeQuestion.TEST_QUESTION_RID, value: JSON.parse( JSON.stringify(this.answerData)) } );

            
            
            this.postAnswer( { 
                    "key": this.answerData.questionId,
                    "data": JSON.stringify(this.answerData),
                    "queryParams":{},
                    "routeParams":{ 
                        action: "answer-question", 
                        testRid: this.$route.params.testRid
                    } 
                }
            ).then(()=>{
                // clear this answer from unsaved
                this.setUnsaved( { key: this.activeQuestion.TEST_QUESTION_RID, value: "" } );
            });
        },
        submitAnswer (){
            if( !this.activeQuestion ) return;
            // clear this answer from unsaved
            this.setUnsaved( { key: this.activeQuestion.TEST_QUESTION_RID, value: "" } );

            this.scoreAnswer( { 
                    "key": this.answerData.questionId,
                    "data": JSON.stringify(this.answerData),
                    "queryParams":{},
                    "routeParams":{ 
                        action: "score-question", 
                        testRid: this.$route.params.testRid
                    } 
                }
            ).then(resp=>{
                this.$set(this.answerData, "questionId" , this.activeQuestion.TEST_QUESTION_RID);
                this.$set(this.answerData, "testId", this.activeQuestion.TEST_RID);
                this.$set(this.answerData, "answer", JSON.parse( JSON.stringify( this.studentAnswer ) ));
            });
        }
    }
};
</script>
